import { useState } from "react";
import { UtensilsCrossed, Search, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { usePoll } from "@/hooks/usePoll";
import { api, rp, imgSrc } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import { PageHeader, Panel, Empty } from "@/components/common";
import { CrudActions, AddButton, confirmDelete, FormDialog, TextField, Field } from "@/components/Crud";

const EMPTY = { name: "", price: "", category_id: "", subcategory_id: "none", description: "", image_url: "", available: true };

export default function Menus() {
  const { data, reload } = usePoll("/menus", 0);
  const cats = usePoll("/categories", 0).data || [];
  const subs = usePoll("/subcategories", 0).data || [];
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openForm = (m) => {
    setEdit(m || {});
    setForm(m ? { ...m, price: String(m.price), subcategory_id: m.subcategory_id || "none" } : { ...EMPTY, category_id: cats[0]?.id || "" });
  };
  const save = async () => {
    if (!form.category_id) throw new Error("Pilih kategori");
    const body = { ...form, price: Number(form.price), subcategory_id: form.subcategory_id === "none" ? null : form.subcategory_id };
    delete body.id; delete body.created_at;
    if (edit.id) await api.put(`/menus/${edit.id}`, body);
    else await api.post("/menus", body);
    toast.success("Menu disimpan");
    reload();
  };
  const toggle = async (m) => {
    const { id, created_at, ...rest } = m;
    await api.put(`/menus/${id}`, { ...rest, available: !m.available });
    reload();
  };
  const nameOf = (list, id) => list.find((x) => x.id === id)?.name || "-";
  const rows = (data || []).filter((m) => (cat === "all" || m.category_id === cat) && m.name.toLowerCase().includes(q.toLowerCase()));
  const formSubs = subs.filter((s) => s.category_id === form.category_id);

  return (
    <div>
      <PageHeader title="Buat Menu" subtitle="Kelola daftar menu, harga, dan ketersediaan">
        <AddButton testId="add-menu-btn" label="Tambah Menu" onClick={() => openForm()} />
      </PageHeader>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" /><Input data-testid="menu-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari menu..." className="bg-white pl-9" /></div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-full bg-white sm:w-48" data-testid="menu-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Kategori</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Panel className="overflow-x-auto">
        {rows.length === 0 ? <Empty icon={UtensilsCrossed} text="Belum ada menu" /> : (
          <Table data-testid="menu-table">
            <TableHeader><TableRow><TableHead>Menu</TableHead><TableHead>Kategori</TableHead><TableHead>Sub Kategori</TableHead><TableHead className="text-right">Harga</TableHead><TableHead>Tersedia</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((m) => (
                <TableRow key={m.id} data-testid={`menu-row-${m.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {m.image_url ? <img src={imgSrc(m.image_url)} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <span className="grid h-10 w-10 place-items-center rounded-lg bg-stone-100"><ImageOff className="h-4 w-4 text-stone-400" /></span>}
                      <span className="font-semibold">{m.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{nameOf(cats, m.category_id)}</TableCell>
                  <TableCell>{nameOf(subs, m.subcategory_id)}</TableCell>
                  <TableCell className="text-right font-mono">{rp(m.price)}</TableCell>
                  <TableCell><Switch data-testid={`menu-available-${m.id}`} checked={m.available} onCheckedChange={() => toggle(m)} /></TableCell>
                  <TableCell><CrudActions id={`menu-${m.id}`} onEdit={() => openForm(m)} onDelete={() => confirmDelete(`/menus/${m.id}`, reload, "menu")} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
      <FormDialog testId="menu-form-dialog" open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Ubah Menu" : "Tambah Menu"} onSubmit={save}>
        <TextField label="Nama Menu" testId="menu-name-input" required value={form.name} onChange={(e) => set("name", e.target.value)} />
        <TextField label="Harga (Rp)" testId="menu-price-input" type="number" min="0" required value={form.price} onChange={(e) => set("price", e.target.value)} />
        <Field label="Kategori">
          <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v, subcategory_id: "none" })}>
            <SelectTrigger data-testid="menu-form-category-select"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
            <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Sub Kategori">
          <Select value={form.subcategory_id} onValueChange={(v) => set("subcategory_id", v)}>
            <SelectTrigger data-testid="menu-form-subcategory-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">Tanpa sub kategori</SelectItem>{formSubs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Foto Menu (opsional)"><ImageUpload value={form.image_url} onChange={(v) => set("image_url", v)} /></Field>
        <Field label="Deskripsi"><Textarea data-testid="menu-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} /></Field>
        <div className="flex items-center gap-3"><Switch data-testid="menu-form-available" checked={form.available} onCheckedChange={(v) => set("available", v)} /><span className="text-sm">Tersedia untuk dipesan</span></div>
      </FormDialog>
    </div>
  );
}
